import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import {
  useSaveCompany,
  useSaveCompanyAddress,
} from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";

export const REGIONS = ["Luzon", "Visayas", "Mindanao"];
// CompanyType enum (server title-cases it)
export const COMPANY_TYPES = ["Division", "Subsidiary"];
export const ADDRESS_TYPES = ["Main", "Billing", "Shipping"];
const messagesOf = (err, fallback) => {
  const m = err?.response?.data?.message;
  return Array.isArray(m) ? m : [getApiErrorMessage(err, fallback)];
};
const Errors = ({ errors }) =>
  errors.length === 0 ? null : (
    <div className="alert alert-danger fs-085 py-2">
      {errors.length === 1 ? (
        errors[0]
      ) : (
        <ul className="mb-0 ps-3">
          {errors.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      )}
    </div>
  );

/** Ids of a node and everything under it (a company can't move under itself). */
export const subtreeIds = (node) => [
  node.id,
  ...(node.children || []).flatMap(subtreeIds),
];

/**
 * Create / edit a company. parentId "" = top-level (super admin only).
 * `options` = flat list [{ id, companyName, depth }] to pick the parent from.
 */
export const CompanyFormDialog = ({
  company,
  parentId,
  options,
  superAdmin,
  onClose,
}) => {
  const isEdit = !!company?.id;
  const [form, setForm] = useState(() => ({
    companyName: company?.companyName || "",
    type:
      COMPANY_TYPES.find(
        (t) => t.toLowerCase() === String(company?.type || "").toLowerCase(),
      ) || "",
    region: company?.region || "",
    parentId: isEdit ? company.parentId || "" : parentId || "",
    isActive: company?.isActive ?? true,
  }));
  const [errors, setErrors] = useState([]);
  const save = useSaveCompany();
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const blocked = useMemo(
    () => new Set(isEdit ? company.subtree || [company.id] : []),
    [isEdit, company],
  );
  const topLevelNotAllowed =
    !form.parentId && !superAdmin && !(isEdit && !company.parentId);

  const submit = () => {
    if (!form.companyName.trim()) return setErrors(["Enter the company name."]);
    if (topLevelNotAllowed)
      return setErrors([
        "Only the super admin can create a top-level company. Choose a parent company.",
      ]);
    setErrors([]);
    save.mutate(
      {
        id: company?.id,
        companyName: form.companyName.trim(),
        type: form.type || null,
        region: form.region || undefined,
        parentId: form.parentId || null,
        isActive: form.isActive,
      },
      {
        onSuccess: () => {
          toast.success(
            isEdit ? "Company saved." : `${form.companyName.trim()} created.`,
          );
          onClose();
        },
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the company.")),
      },
    );
  };

  return (
    <Dialog
      title={
        isEdit
          ? `Edit ${company.companyName}`
          : form.parentId
            ? "New sub-company"
            : "New top-level company"
      }
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={save.isPending}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {isEdit ? "Save" : "Create"}
          </button>
        </>
      }
    >
      <Errors errors={errors} />
      <div className="row g-3">
        <div className="col-12">
          <label htmlFor="co-name" className="profile-label">
            Company name <span className="text-danger">*</span>
          </label>
          <input
            id="co-name"
            className="form-control"
            value={form.companyName}
            onChange={(e) => set({ companyName: e.target.value })}
            maxLength={150}
            autoFocus
          />
        </div>
        <div className="col-md-6">
          <label htmlFor="co-type" className="profile-label">
            Type
          </label>
          <select
            id="co-type"
            className="form-select"
            value={form.type}
            onChange={(e) => set({ type: e.target.value })}
          >
            <option value="">—</option>
            {COMPANY_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label htmlFor="co-region" className="profile-label">
            Region
          </label>
          <select
            id="co-region"
            className="form-select"
            value={form.region}
            onChange={(e) => set({ region: e.target.value })}
          >
            <option value="">—</option>
            {REGIONS.map((r) => (
              <option key={r}>{r}</option>
            ))}
          </select>
        </div>
        <div className="col-12">
          <label htmlFor="co-parent" className="profile-label">
            Parent company
          </label>
          <select
            id="co-parent"
            className="form-select"
            value={form.parentId}
            onChange={(e) => set({ parentId: e.target.value })}
          >
            <option
              value=""
              disabled={!superAdmin && !(isEdit && !company.parentId)}
            >
              None (top-level{superAdmin ? "" : ": super admin only"})
            </option>
            {options.map((o) => (
              <option key={o.id} value={o.id} disabled={blocked.has(o.id)}>
                {"  ".repeat(o.depth)}
                {o.companyName}
              </option>
            ))}
          </select>
          {isEdit && (
            <div className="fs-070 text-slate-500 mt-1">
              Changing the parent moves the company (and its sub-companies) in
              the tree.
            </div>
          )}
        </div>
        <div className="col-12">
          <div className="form-check form-switch">
            <input
              id="co-active"
              type="checkbox"
              className="form-check-input"
              checked={form.isActive}
              onChange={(e) => set({ isActive: e.target.checked })}
            />
            <label htmlFor="co-active" className="form-check-label fs-085">
              Active
            </label>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

const EMPTY_ADDRESS = {
  type: "Main",
  addressLine1: "",
  addressLine2: "",
  city: "",
  province: "",
  state: "",
  postalCode: "",
  phoneNumber: "",
  faxNumber: "",
  email: "",
  supportEmail: "",
  isPrimary: false,
  isActive: true,
};

/** Add / edit one company address. */
export const AddressDialog = ({ companyId, address, onClose }) => {
  const isEdit = !!address?.id;
  const [form, setForm] = useState(() => ({
    ...EMPTY_ADDRESS,
    ...Object.fromEntries(
      Object.entries(address || {}).map(([k, v]) => [
        k,
        v ?? (typeof EMPTY_ADDRESS[k] === "boolean" ? false : ""),
      ]),
    ),
  }));
  const [errors, setErrors] = useState([]);
  const save = useSaveCompanyAddress();
  const set = (changes) => setForm((f) => ({ ...f, ...changes }));
  const field = (
    key,
    label,
    { required, type = "text", col = "col-md-6" } = {},
  ) => (
    <div className={col}>
      <label htmlFor={`addr-${key}`} className="profile-label">
        {label}
        {required && <span className="text-danger"> *</span>}
      </label>
      <input
        id={`addr-${key}`}
        type={type}
        className="form-control"
        value={form[key]}
        onChange={(e) => set({ [key]: e.target.value })}
      />
    </div>
  );

  const submit = () => {
    const missing = [
      ["addressLine1", "Address line 1"],
      ["city", "City"],
      ["province", "Province"],
      ["postalCode", "Postal code"],
    ].filter(([k]) => !String(form[k]).trim());
    if (missing.length)
      return setErrors([`Fill in: ${missing.map(([, l]) => l).join(", ")}.`]);
    setErrors([]);
    const body = Object.fromEntries(
      Object.entries(form)
        .filter(([k]) => k in EMPTY_ADDRESS)
        .map(([k, v]) => [
          k,
          typeof v === "string" ? v.trim() || undefined : v,
        ]),
    );
    save.mutate(
      { companyId, addressId: address?.id, ...body },
      {
        onSuccess: () => {
          toast.success(isEdit ? "Address saved." : "Address added.");
          onClose();
        },
        onError: (err) =>
          setErrors(messagesOf(err, "Could not save the address.")),
      },
    );
  };

  return (
    <Dialog
      title={isEdit ? "Edit address" : "Add address"}
      size="lg"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary profile-btn"
            onClick={submit}
            disabled={save.isPending}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            Save
          </button>
        </>
      }
    >
      <Errors errors={errors} />
      <div className="row g-3">
        <div className="col-md-4">
          <label htmlFor="addr-type" className="profile-label">
            Type
          </label>
          <select
            id="addr-type"
            className="form-select"
            value={form.type}
            onChange={(e) => set({ type: e.target.value })}
          >
            {ADDRESS_TYPES.map((t) => (
              <option key={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="col-md-8 d-flex align-items-end gap-3">
          <div className="form-check form-switch">
            <input
              id="addr-primary"
              type="checkbox"
              className="form-check-input"
              checked={!!form.isPrimary}
              onChange={(e) => set({ isPrimary: e.target.checked })}
            />
            <label htmlFor="addr-primary" className="form-check-label fs-085">
              Primary {form.type} address
            </label>
          </div>
          <div className="form-check form-switch">
            <input
              id="addr-active"
              type="checkbox"
              className="form-check-input"
              checked={form.isActive !== false}
              onChange={(e) => set({ isActive: e.target.checked })}
            />
            <label htmlFor="addr-active" className="form-check-label fs-085">
              Active
            </label>
          </div>
        </div>
        {field("addressLine1", "Address line 1", {
          required: true,
          col: "col-12",
        })}
        {field("addressLine2", "Address line 2", { col: "col-12" })}
        {field("city", "City", { required: true })}
        {field("province", "Province", { required: true })}
        {field("state", "State")}
        {field("postalCode", "Postal code", { required: true })}
        {field("phoneNumber", "Phone")}
        {field("faxNumber", "Fax")}
        {field("email", "Email", { type: "email" })}
        {field("supportEmail", "Support email", { type: "email" })}
      </div>
      <p className="fs-070 text-slate-500 mt-2 mb-0">
        The first address of a type becomes its primary one; marking another as
        primary replaces it.
      </p>
    </Dialog>
  );
};
